import React, { FunctionComponent, JSX, useCallback, useState } from 'react'
import { FormControlLabel, IconButton, Menu, MenuItem as MuiMenuItem, PaperProps, PopoverOrigin } from '@mui/material'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import Checkbox from '@/components/_custom/Checkbox/Checkbox'
import styles from './sectionSelector.module.scss'

export interface MenuItem {
    id: number
    label: string
    isChecked: boolean
}

interface SectionMenuProps {
    sectionName: string
    items: MenuItem[]
    onSectionChange: () => void
    onItemChange: (itemId: number) => void
    sortFn?: (a: MenuItem, b: MenuItem) => number
}

const menuPaperProps: Partial<PaperProps> = {
    elevation: 0,
    sx: {
        overflow: 'visible',
        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
        mt: 1.5,
        maxHeight: 250,
        maxWidth: 500,
        overflowY: 'auto'
    }
}

const menuTransformOrigin: PopoverOrigin = { horizontal: 'right', vertical: 'top' }
const menuAnchorOrigin: PopoverOrigin = { horizontal: 'right', vertical: 'bottom' }

const SectionMenu: FunctionComponent<SectionMenuProps> = ({
    sectionName,
    items,
    onSectionChange,
    onItemChange,
    sortFn
}: SectionMenuProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const isOpen: boolean = Boolean(anchorEl)

    const handleClick: (event: React.MouseEvent<HTMLElement>) => void = useCallback(
        (event: React.MouseEvent<HTMLElement>): void => {
            setAnchorEl(event.currentTarget)
        },
        []
    )

    const handleClose: () => void = useCallback((): void => {
        setAnchorEl(null)
    }, [])

    const allChecked: boolean = items.every((item: MenuItem): boolean => item.isChecked)
    const someChecked: boolean = items.some((item: MenuItem): boolean => item.isChecked)
    const isIndeterminate: boolean = !allChecked && someChecked

    const sortedItems: MenuItem[] = sortFn ? [...items].sort(sortFn) : items

    return (
        <div className={styles.section}>
            <FormControlLabel
                control={
                    <Checkbox
                        text={sectionName}
                        checked={allChecked}
                        onChange={onSectionChange}
                        indeterminate={isIndeterminate}
                    />
                }
                label=''
            />
            <IconButton onClick={handleClick}>{isOpen ? <ExpandLess /> : <ExpandMore />}</IconButton>
            <Menu
                anchorEl={anchorEl}
                open={isOpen}
                onClose={handleClose}
                slotProps={{ paper: menuPaperProps }}
                transformOrigin={menuTransformOrigin}
                anchorOrigin={menuAnchorOrigin}
            >
                {sortedItems.map(
                    (item: MenuItem): JSX.Element => (
                        <MuiMenuItem key={item.id} onClick={(): void => onItemChange(item.id)}>
                            <Checkbox text={item.label} checked={item.isChecked} className={styles.child} />
                        </MuiMenuItem>
                    )
                )}
            </Menu>
        </div>
    )
}

export default SectionMenu
